import React, { useState } from "react";
import { Separator } from "../ui/separator";
import { personalInfo } from "../../data/mock";
import {
  Mail,
  MapPin,
  Phone,
  Linkedin,
  Github,
  ExternalLink,
  Send,
  ArrowUpRight,
  BookOpen,
  GraduationCap,
  ChevronUp,
} from "lucide-react";

/* ─── Contact Section ─── */
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission — saves to localStorage
    const messages = JSON.parse(localStorage.getItem("contactMessages") || "[]");
    messages.push({ ...formData, timestamp: new Date().toISOString() });
    localStorage.setItem("contactMessages", JSON.stringify(messages));
    setSubmitted(true);
    setFormData({ name: "", email: "", subject: "", message: "" });
    setTimeout(() => setSubmitted(false), 4000);
  };

  return (
    <section
      id="contact"
      className="py-24 lg:py-32"
      style={{ background: "linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)" }}
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-sm font-semibold text-teal-700 uppercase tracking-widest">
            Contact
          </span>
          <h2
            className="text-3xl lg:text-4xl font-bold text-slate-900 mt-3"
            style={{ fontFamily: "'Source Serif 4', serif" }}
          >
            Travaillons Ensemble
          </h2>
          <p className="text-slate-500 mt-4 max-w-xl mx-auto">
            Intéressé par une collaboration, un projet de recherche ou une mission d'analyse de données ?
            N'hésitez pas à me contacter.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-6">
            <div className="space-y-5">
              <a
                href={`mailto:${personalInfo.email}`}
                className="flex items-center gap-4 group"
              >
                <div className="w-11 h-11 rounded-lg bg-teal-50 flex items-center justify-center group-hover:bg-teal-100 transition-colors duration-200">
                  <Mail className="w-5 h-5 text-teal-700" />
                </div>
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wider">E-mail</p>
                  <p className="text-sm font-medium text-slate-700 group-hover:text-teal-700 transition-colors duration-200">
                    {personalInfo.email}
                  </p>
                </div>
              </a>

              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-lg bg-slate-100 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-slate-500" />
                </div>
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wider">Localisation</p>
                  <p className="text-sm font-medium text-slate-700">
                    {personalInfo.location}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-slate-500" />
                </div>
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wider">Téléphone</p>
                  <p className="text-sm font-medium text-slate-700">
                    {personalInfo.phone}
                  </p>
                </div>
              </div>
            </div>

            <Separator className="bg-slate-200" />

            <div>
              <p className="text-sm font-semibold text-slate-800 mb-3">Profils Académiques</p>
              <div className="flex flex-wrap gap-3">
                {[
                  { icon: Linkedin, label: "LinkedIn", href: personalInfo.linkedin },
                  { icon: Github, label: "GitHub", href: personalInfo.github },
                  { icon: GraduationCap, label: "ResearchGate", href: personalInfo.researchgate },
                  { icon: BookOpen, label: "ORCID", href: personalInfo.orcid },
                ].map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-600 hover:text-teal-700 hover:border-teal-200 hover:shadow-sm transition-all duration-200"
                  >
                    <link.icon className="w-4 h-4" />
                    {link.label}
                    <ArrowUpRight className="w-3 h-3 opacity-50" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            <form
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl p-8 border border-slate-200/80 shadow-sm"
            >
              <div className="grid sm:grid-cols-2 gap-5 mb-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Nom
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Votre nom"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 transition-all duration-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    E-mail
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="you@example.com"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 transition-all duration-200"
                  />
                </div>
              </div>

              <div className="mb-5">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Objet
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  placeholder="Collaboration, conseil, etc."
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 transition-all duration-200"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Message
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  placeholder="Parlez-moi de votre projet ou de votre recherche..."
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 transition-all duration-200 resize-none"
                />
              </div>

              <button
                type="submit"
                className="flex items-center justify-center gap-2 w-full px-6 py-3.5 bg-teal-700 hover:bg-teal-800 text-white font-medium rounded-lg transition-colors duration-200"
              >
                <Send className="w-4 h-4" />
                Send Message
              </button>

              {submitted && (
                <div className="mt-4 p-4 bg-teal-50 border border-teal-200 rounded-lg text-sm text-teal-700 text-center">
                  Thank you! Your message has been sent. I'll get back to you soon.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─── Footer ─── */
const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="bg-slate-900 text-slate-400">
      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-12">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-teal-700 flex items-center justify-center">
                <span className="text-white font-bold text-sm font-mono">AD</span>
              </div>
              <span
                className="font-semibold text-white text-sm"
                style={{ fontFamily: "'Source Serif 4', serif" }}
              >
                {personalInfo.name}
              </span>
            </div>
            <p className="text-sm text-slate-500 max-w-sm">
              Freelance Data Scientist specializing in geotechnics and rock mechanics.
              Turning complex subsurface data into clear, actionable insights.
            </p>
          </div>

          <div className="flex flex-wrap gap-6 text-sm">
            {["About", "Expertise", "Publications", "Conferences", "Contact"].map((link) => (
              <a
                key={link}
                href={`#${link.toLowerCase()}`}
                onClick={(e) => {
                  e.preventDefault();
                  document
                    .querySelector(`#${link.toLowerCase()}`)
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
                className="text-slate-500 hover:text-teal-400 transition-colors duration-200"
              >
                {link}
              </a>
            ))}
          </div>

          <button
            onClick={scrollToTop}
            className="p-3 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-all duration-200"
            title="Back to top"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
        </div>

        <Separator className="my-8 bg-slate-800" />

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-600">
          <p>© {new Date().getFullYear()} {personalInfo.name}. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <a
              href={personalInfo.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-slate-400 transition-colors duration-200"
            >
              LinkedIn
            </a>
            <a
              href={personalInfo.github}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-slate-400 transition-colors duration-200"
            >
              GitHub
            </a>
            <a
              href={personalInfo.researchgate}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-slate-400 transition-colors duration-200"
            >
              ResearchGate
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

/* ─── Combined Export ─── */
const ContactFooter = () => (
  <>
    <ContactSection />
    <Footer />
  </>
);

export default ContactFooter;
